/* eslint-disable */
import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { TableIcon } from '@heroicons/react/outline';
import { AiOutlineBarChart, AiOutlineLineChart } from 'react-icons/ai';

import Card from 'partial/components/Card';
import Table from 'partial/components/Table';
import Button from 'partial/components/Button';
import BarChart from 'modules/common/chart/BarChart';
import LineChart from 'modules/common/chart/LineChart';
import HistogramRange from 'partial/components/HistogramRange';

export const useHistogramCard = (array, default_index = 0) => {
  const [props, setProps] = React.useState(array[default_index]);
  const output = React.useMemo(
    () => ({
      key: props.id,
      ...props,
      actionOptions: array.map((x) => ({
        id: x.id,
        label: x.label,
        onClick: () => {
          setProps(x);
        },
      })),
    }),
    [array, props, setProps]
  );
  return output;
};

const MAP_TYPE = {
  day: 'DAILY',
  month: 'MONTHLY',
  year: 'YEARLY',
};

function HistogramCard({
  hook,
  label,
  dateRange,
  switchMode,
  defaultSort,
  defaultMode,
  tableFormat,
  defaultChart,
  actionOptions,
  minDate,
  maxDate,
  filter: otherFilter,
  downloadCsvUrl,
  downloadCsvFilename,
  seriesName,
  lineChartOptions,
  barChartOptions,
  height,
}) {
  const [chartType, setChartType] = React.useState(defaultChart);
  const [meta, setMeta] = React.useState({ sort: defaultSort });
  const [filter, setFilter] = React.useState({
    type: MAP_TYPE[defaultMode],
    from: dayjs().startOf(defaultMode).format('YYYY-MM-DD 00:00:00'),
    to: dayjs().endOf(defaultMode).format('YYYY-MM-DD 23:59:59'),
    year: dayjs().format('YYYY'),
  });
  const newFilter = React.useMemo(
    () => ({
      ...filter,
      ...otherFilter,
    }),
    [otherFilter, filter]
  );
  const [isLoading, table, chart, subLabel] = hook(newFilter);
  const toggleChart = React.useMemo(
    () => (
      <div className="grid grid-cols-1 sm:flex gap-1 items-center">
        {dateRange && (
          <div className="space-x-1 flex border border-primary-800">
            <HistogramRange
              onChange={setFilter}
              defaultValue={dayjs().format('YYYY-MM-DD')}
              defaultMode={defaultMode}
              switchMode={switchMode}
              minDate={minDate}
              maxDate={maxDate}
            />
          </div>
        )}
        <div className="space-x-1 flex justify-end">
          <Button
            size="sm"
            primary={chartType === 'table'}
            outlinePrimary={chartType !== 'table'}
            icon={TableIcon}
            onClick={() => setChartType('table')}
          />
          <Button
            size="sm"
            primary={chartType === 'bar'}
            outlinePrimary={chartType !== 'bar'}
            icon={AiOutlineBarChart}
            onClick={() => setChartType('bar')}
          />
          <Button
            size="sm"
            primary={chartType === 'line'}
            outlinePrimary={chartType !== 'line'}
            icon={AiOutlineLineChart}
            onClick={() => setChartType('line')}
          />
        </div>
      </div>
    ),
    [setFilter, chartType, setChartType, defaultMode, switchMode, dateRange]
  );
  return (
    <Card
      label={label}
      actionOptions={actionOptions}
      renderAction={toggleChart}
    >
      <div className="flex flex-col pr-3" style={{ height: height }}>
        {chartType === 'table' && (
          <Table
            isLoading={isLoading}
            data={table}
            format={tableFormat}
            downloadCsvUrl={downloadCsvUrl}
            downloadFilename={downloadCsvFilename}
            onSort={meta.sort.indexOf('id') > -1 ? false : setMeta}
            sort={meta.sort}
            subLabel={subLabel}
          />
        )}
        {chartType === 'line' && (
          <LineChart
            data={chart}
            isLoading={isLoading}
            seriesName={seriesName}
            chartOptions={lineChartOptions}
          />
        )}
        {chartType === 'bar' && (
          <BarChart
            data={chart}
            horizontal={false}
            isLoading={isLoading}
            seriesName={seriesName}
            chartOptions={barChartOptions}
          />
        )}
      </div>
    </Card>
  );
}

HistogramCard.defaultProps = {
  filter: {},
  label: 'Chart',
  dateRange: true,
  switchMode: true,
  actionOptions: [],
  defaultMode: 'year',
  defaultChart: 'line',
  defaultSort: 'id:asc',
  tableFormat: [
    { key: 'label', label: 'Name' },
    { key: 'amount', label: 'Amount', width: 120 },
  ],
  downloadCsvUrl: '',
  downloadCsvFilename: '',
  minDate: null,
  maxDate: null,
  seriesName: '',
  lineChartOptions: {},
  barChartOptions: {},
  height: 300,
};

HistogramCard.propTypes = {
  label: PropTypes.string,
  dateRange: PropTypes.bool,
  switchMode: PropTypes.bool,
  defaultMode: PropTypes.string,
  defaultSort: PropTypes.string,
  defaultChart: PropTypes.string,
  hook: PropTypes.func.isRequired,
  filter: PropTypes.instanceOf(Object),
  tableFormat: PropTypes.instanceOf(Array),
  actionOptions: PropTypes.instanceOf(Array),
  downloadCsvUrl: PropTypes.string,
  downloadCsvFilename: PropTypes.string,
  minDate: PropTypes.string,
  maxDate: PropTypes.string,
  seriesName: PropTypes.string,
  lineChartOptions: PropTypes.instanceOf(Object),
  barChartOptions: PropTypes.instanceOf(Object),
  height: PropTypes.number,
};

export default HistogramCard;
